import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  apiClient,
  type NotificationPreference,
  type NotificationTemplateCatalogItem,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';

interface PreferenceRow {
  template: NotificationTemplateCatalogItem;
  inAppEnabled: boolean;
  emailEnabled: boolean;
}

/**
 * F05 — Pagina de preferencias de notificacoes.
 *
 * Tabela: EVENTO | IN-APP | EMAIL. Default sao ambos true se nao houver
 * preferencia salva no banco. "Restaurar padroes" volta tudo a true.
 */
export function NotificationsPreferencesPage() {
  const queryClient = useQueryClient();

  const catalogQuery = useQuery({
    queryKey: ['notifications', 'templates-catalog'],
    queryFn: () => apiClient.notificationsTemplatesCatalog(),
    staleTime: 5 * 60_000,
  });

  const prefsQuery = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => apiClient.notificationsPreferencesList(),
    staleTime: 30_000,
  });

  const upsertMut = useMutation({
    mutationFn: (input: {
      templateKey: string;
      inAppEnabled: boolean;
      emailEnabled: boolean;
    }) =>
      apiClient.notificationsPreferencesUpsert(
        input.templateKey,
        input.inAppEnabled,
        input.emailEnabled,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['notifications', 'preferences'],
      });
    },
  });

  const rows: PreferenceRow[] = useMemo(() => {
    const templates = catalogQuery.data?.items ?? [];
    const prefsByKey = new Map<string, NotificationPreference>();
    for (const p of prefsQuery.data?.items ?? []) {
      prefsByKey.set(p.templateKey, p);
    }
    return templates.map((t) => {
      const p = prefsByKey.get(t.templateKey);
      return {
        template: t,
        // Default: ambos ativos se nao houver registro.
        inAppEnabled: p?.inAppEnabled ?? true,
        emailEnabled: p?.emailEnabled ?? true,
      };
    });
  }, [catalogQuery.data, prefsQuery.data]);

  function toggle(row: PreferenceRow, channel: 'inApp' | 'email') {
    upsertMut.mutate({
      templateKey: row.template.templateKey,
      inAppEnabled: channel === 'inApp' ? !row.inAppEnabled : row.inAppEnabled,
      emailEnabled: channel === 'email' ? !row.emailEnabled : row.emailEnabled,
    });
  }

  async function restoreDefaults() {
    if (
      !window.confirm(
        'Restaurar todas as notificações para o padrão (in-app e email ativos)?',
      )
    )
      return;
    const items = catalogQuery.data?.items ?? [];
    // Sequencial pra nao bombardear o backend; rapido o suficiente pra <50
    // templates. Se virar gargalo, vira endpoint dedicado depois.
    for (const t of items) {
      await apiClient.notificationsPreferencesUpsert(
        t.templateKey,
        true,
        true,
      );
    }
    void queryClient.invalidateQueries({
      queryKey: ['notifications', 'preferences'],
    });
  }

  const loading = catalogQuery.isLoading || prefsQuery.isLoading;
  const hasError = catalogQuery.isError || prefsQuery.isError;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
            Notificações
          </h1>
          <p className="mt-1 text-sm text-[var(--fg-muted)]">
            Escolha por evento se você quer ser avisado dentro do app (sino) e/ou por email.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={restoreDefaults}
          disabled={loading || hasError || upsertMut.isPending}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Restaurar padrões
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando...
          </p>
        ) : hasError ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Bell className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Não foi possível carregar as preferências no momento.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Nenhum tipo de notificação disponível.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Evento</th>
                <th className="w-24 px-4 py-2 text-center font-medium">
                  In-app
                </th>
                <th className="w-24 px-4 py-2 text-center font-medium">
                  Email
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.template.templateKey}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--fg)]">
                      {row.template.label}
                    </p>
                    <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
                      {row.template.description}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      checked={row.inAppEnabled}
                      onChange={() => toggle(row, 'inApp')}
                      label={`Notificação in-app para ${row.template.label}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      checked={row.emailEnabled}
                      onChange={() => toggle(row, 'email')}
                      label={`Notificação por email para ${row.template.label}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition',
        checked
          ? 'bg-[var(--accent)]'
          : 'bg-[color-mix(in_oklch,var(--fg)_15%,transparent)]',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition',
          checked ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
